/**
 * ErrorModal Component - Modal dialog for non-retryable errors
 * Displays error message, Error ID with copy button, and action buttons
 */

import React, { useEffect, useState, useCallback } from "react";

interface ErrorModalProps {
  title?: string;
  message: string;
  footer: string;
  buttons?: Array<{ label: string; onClick?: () => void }>;
  onClose?: () => void;
  isOpen?: boolean;
}

/**
 * ErrorModal Component
 */
export const ErrorModal: React.FC<ErrorModalProps> = ({
  title = "Error",
  message,
  footer,
  buttons = [{ label: "OK" }],
  onClose,
  isOpen = true,
}) => {
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(isOpen);

  // Extract Error ID from footer text (format: "Error ID: {id} — ...")
  const errorIdMatch = footer.match(/Error ID: ([^\s—]+)/);
  const errorId = errorIdMatch ? errorIdMatch[1] : "";

  const handleClose = useCallback(() => {
    setIsVisible(false);
    onClose?.();
  }, [onClose]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      window.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isVisible, handleClose]);

  const handleCopyErrorId = async () => {
    if (errorId) {
      try {
        await navigator.clipboard.writeText(errorId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy Error ID:", err);
      }
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 transform rounded-lg bg-white shadow-xl"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2
            id="error-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            {title}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p id="error-modal-description" className="text-sm text-gray-700">
            {message}
          </p>
        </div>

        {/* Footer with Error ID */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600">Error ID</p>
              <code className="mt-1 block break-all font-mono text-sm text-gray-900">
                {errorId || footer}
              </code>
              {!errorIdMatch && (
                <p className="mt-2 text-xs text-gray-600">{footer}</p>
              )}
            </div>
            {errorId && (
              <div className="ml-4 flex-shrink-0 flex gap-2">
                <button
                  onClick={handleCopyErrorId}
                  className="rounded bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  title="Copy Error ID to clipboard"
                >
                  {copied ? (
                    <span className="text-green-600">✓ Copied</span>
                  ) : (
                    <span>Copy</span>
                  )}
                </button>
                {/* MCP Query Link Button */}
                <button
                  onClick={() => {
                    // Trigger the Kiro MCP error_query tool with this error ID
                    // This opens the MCP tool UI or logs the command
                    if (
                      typeof window !== "undefined" &&
                      (window as any).__kiroMCP
                    ) {
                      (window as any).__kiroMCP.queryError(errorId);
                    } else {
                      console.log(`[MCP] Query error ID: ${errorId}`);
                      // Fallback: show message that MCP is available via command palette
                      alert(
                        `Error ID: ${errorId}\n\nYou can query this error using the Kiro MCP error_query tool.\nError ID is copied to clipboard.`,
                      );
                    }
                  }}
                  className="rounded bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-100"
                  title="Query this error via Kiro MCP"
                >
                  Query Error
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <div className="flex justify-end gap-3">
            {buttons.map((button, idx) => (
              <button
                key={idx}
                onClick={() => {
                  button.onClick?.();
                  handleClose();
                }}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

ErrorModal.displayName = "ErrorModal";
